import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook } from 'react-sweet-state';

import type { CommentData } from '@confluence/comments-data';

export enum CurrentView {
	OPEN = 'OPEN',
	UNREAD = 'UNREAD',
	RESOLVED = 'RESOLVED',
}

type CommentsPanelView = {
	comments: CommentData[];
	isLoading: boolean;
	hasError: boolean;
};

export type CommentsPanelState = {
	currentView: CurrentView | undefined;
	viewStates: Record<CurrentView, CommentsPanelView>;
	currentlySelectedCommentMarkerRef: string | undefined;
	isCommentsPanelOpen: boolean;
	commentIdsToMarkAsRead: string[];
};

const initialState: CommentsPanelState = {
	currentView: undefined,
	viewStates: {
		[CurrentView.OPEN]: {
			comments: [],
			isLoading: false,
			hasError: false,
		},
		[CurrentView.UNREAD]: {
			comments: [],
			isLoading: false,
			hasError: false,
		},
		[CurrentView.RESOLVED]: {
			comments: [],
			isLoading: false,
			hasError: false,
		},
	},
	currentlySelectedCommentMarkerRef: undefined,
	isCommentsPanelOpen: false,
	commentIdsToMarkAsRead: [],
};

// Actions for managing the comments state in the comments panel
const actions = {
	setCurrentView:
		(view: CurrentView) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentView: view });
		},
	setCurrentlySelectedCommentMarkerRef:
		(markerRef: string | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ currentlySelectedCommentMarkerRef: markerRef });
		},
	setIsCommentsPanelOpen:
		(isOpen: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>) => {
			setState({ isCommentsPanelOpen: isOpen });
		},
	setCommentIdsToMarkAsRead:
		({ commentIds, clearList }: { commentIds: string[]; clearList: boolean }) =>
		({ setState, getState }: StoreActionApi<CommentsPanelState>) => {
			const { commentIdsToMarkAsRead } = getState();
			// combine the existing and ids to mark as read, and remove duplicates using a Set
			const updatedCommentIds = clearList
				? []
				: Array.from(new Set([...commentIdsToMarkAsRead, ...commentIds]));
			setState({ commentIdsToMarkAsRead: updatedCommentIds });
		},
};

const CommentsStore = createStore({
	initialState,
	actions,
	name: 'CommentsStore',
});

export const useCommentsPanel = createHook(CommentsStore);
